import _ from 'lodash'
import React, { ChangeEventHandler } from 'react'
import cn from 'classnames'
import { connect, ConnectedProps } from 'react-redux'
import { getYear } from 'date-fns'
import dynamic from 'next/dynamic'
import getConfig from 'next/config'

import lang from 'lang'
import { cityWithCounty, isUser } from 'common/utils'
import Form from 'common/components/form'
import TrainingPicker from 'common/components/form/training/picker'
import {
  changeSchoolFormField,
  validateField as validateFieldAction,
} from 'page-modules/dashboard/school-creation/service/actions'
import { defaultFormData } from 'page-modules/dashboard/school-creation/service/reducer'
import statesService from 'services/states'
import citiesService from 'services/cities'
import gradesService from 'services/grades'
import phasesService from 'services/phases'
import countriesService from 'services/countries'
import districtsService from 'services/districts'
import { sortCountriesWithUSFirst } from 'common/utils/sort-countries'
import { AppState } from 'common/store/reducer'
import { Validations } from 'common/validator-types'
import type { OfflineTrainingsValidations } from 'common/components/form/training/picker'

const { publicRuntimeConfig } = getConfig() as { publicRuntimeConfig: { FILESTACK_API?: string } }

const ReactFilestack = dynamic(() => import('filestack-react'), { ssr: false })

interface District {
  id: string
  name: string
  country_code?: string
}

interface OfflineTrainingItem {
  offline_training_id: string
  training_attended_at: string
}

type SchoolFormValidations = {
  schoolName: string
  schoolDistrict: string
  websiteUrl: string
  phase: string
  phoneNumber: string
  address: string
  city: string
  state: string
  country: string
  postalCode: string
  schoolGrades: string
  started_ruler_at: string
  total_staff: string
  coachName: string
  coachEmail: string
  salesforceId: string
  offlineTrainings: {
    self: string
    offline_training_id: (opts: {
      value: unknown
      input: Record<string, unknown>
      index: number
    }) => string | null
    training_attended_at: (opts: {
      value: unknown
      input: Record<string, unknown>
      index: number
    }) => string | null
  }
}

const connector = connect(
  (state: AppState) => ({
    user: state.root.user,
    schoolName: state.manageSchool.form.formData.schoolName,
    schoolDistrict: state.manageSchool.form.formData.schoolDistrict,
    schoolGrades: state.manageSchool.form.formData.schoolGrades as string[],
    phase: state.manageSchool.form.formData.phase,
    websiteUrl: state.manageSchool.form.formData.websiteUrl,
    phoneNumber: state.manageSchool.form.formData.phoneNumber,
    address: state.manageSchool.form.formData.address,
    address2: state.manageSchool.form.formData.address2,
    city: state.manageSchool.form.formData.city,
    state: state.manageSchool.form.formData.state,
    country: state.manageSchool.form.formData.country,
    postalCode: state.manageSchool.form.formData.postalCode,
    logoUrl: state.manageSchool.form.formData.logoUrl,
    errors: state.manageSchool.form.errors,
    availableCountries: state.countries.items,
    availableStates: state.states.items,
    availableCities: state.cities.items,
    grades: state.grades.items,
    phases: state.phases.items,
    districts: state.districts.items as District[],
    district: state.district.details.item,
    schoolDistrictName: state.manageSchool.form.formData.schoolDistrictName,
    offlineTrainings: state.manageSchool.form.formData.offlineTrainings as OfflineTrainingItem[],
    started_ruler_at: state.manageSchool.form.formData.started_ruler_at,
    started_ruler: state.manageSchool.form.formData.started_ruler,
    total_staff: state.manageSchool.form.formData.total_staff,
    coachName: state.manageSchool.form.formData.coachName,
    coachEmail: state.manageSchool.form.formData.coachEmail,
    salesforceId: state.manageSchool.form.formData.salesforceId,
  }),
  (dispatch) => ({
    changeFormField: (key: string, value: unknown) => dispatch(changeSchoolFormField(key, value)),
    validateField: (
      field: string | { field: string; value: unknown; validations: keyof Validations },
      value?: unknown,
      validations?: keyof Validations,
    ) =>
      dispatch(
        validateFieldAction(
          typeof field === 'string' ? { field, value: value!, validations: validations! } : field,
        ),
      ),
    countriesRequest: (payload: Parameters<typeof countriesService.actions.request>[0]) =>
      dispatch(countriesService.actions.request(payload)),
    getStatesByCountryRequest: (payload: Parameters<typeof statesService.actions.request>[0]) =>
      dispatch(statesService.actions.request(payload)),
    getCitiesByStateIdRequest: (payload: Parameters<typeof citiesService.actions.request>[0]) =>
      dispatch(citiesService.actions.request(payload)),
    gradesRequest: (payload: Parameters<typeof gradesService.actions.request>[0]) =>
      dispatch(gradesService.actions.request(payload)),
    phasesRequest: (payload: Parameters<typeof phasesService.actions.request>[0]) =>
      dispatch(phasesService.actions.request(payload)),
    districtsRequest: (payload: Parameters<typeof districtsService.actions.request>[0]) =>
      dispatch(districtsService.actions.request(payload)),
  }),
)

type Props = ConnectedProps<typeof connector>

const validations: SchoolFormValidations = {
  schoolName: 'nonEmpty',
  schoolDistrict: 'nonEmpty',
  websiteUrl: 'optional url',
  phase: 'optional',
  phoneNumber: 'optional phoneNumber',
  address: 'optional',
  city: 'nonEmpty',
  state: 'nonEmpty',
  country: 'nonEmpty',
  postalCode: 'optional postalCode',
  schoolGrades: 'nonEmpty',
  started_ruler_at: 'nonEmpty',
  total_staff: 'nonEmpty number',
  coachName: 'optional length160',
  coachEmail: 'optional email',
  salesforceId: 'optional',
  offlineTrainings: {
    self: 'isArray',
    offline_training_id: ({ value, input, index }) => {
      const trainingDate = _.get(input, `offlineTrainings.value[${index}].training_attended_at`)
      return trainingDate && !value ? lang.dashboard.manageSchoolForm.selectTraining : null
    },
    training_attended_at: ({ value, input, index }) => {
      const training = _.get(input, `offlineTrainings.value[${index}].offline_training_id`)
      return training && !value ? lang.dashboard.manageSchoolForm.selectTrainingDate : null
    },
  },
}

class SchoolFormComponent extends (Form as unknown as React.ComponentClass<Props>) {
  static validations = validations

  componentDidMount(): void {
    this.props.countriesRequest({})
    this.props.gradesRequest({})
    this.props.phasesRequest({})
    this.props.districtsRequest({ urlParams: { select: 'id,name,country_code' } })
    if (this.props.country) {
      this.getStatesByCountry(this.props.country)
    }
    if (this.props.state) {
      this.getCitiesByState(this.props.state)
    }
    if (this.props.schoolGrades.length === 0 && this.props.grades?.length) {
      this.props.changeFormField(
        'schoolGrades',
        (this.props.grades as Array<{ id: string }>).map((grade) => grade.id),
      )
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.country !== this.props.country && this.props.country) {
      this.getStatesByCountry(this.props.country)
      this.props.changeFormField('state', undefined)
      this.props.changeFormField('city', undefined)
    }
    if (prevProps.state !== this.props.state && this.props.state) {
      this.getCitiesByState(this.props.state)
    }
    if (
      this.props.schoolGrades.length === 0 &&
      (!prevProps.grades || prevProps.grades.length === 0) &&
      this.props.grades?.length
    ) {
      this.props.changeFormField(
        'schoolGrades',
        (this.props.grades as Array<{ id: string }>).map((grade) => grade.id),
      )
    }
  }

  createStateHandler = (): ChangeEventHandler<HTMLSelectElement> => (e) => {
    this.props.changeFormField('state', e.target.value)
  }

  createCountryHandler = (): ChangeEventHandler<HTMLSelectElement> => (e) => {
    this.props.changeFormField('country', e.target.value)
  }

  createCityHandler = (): ChangeEventHandler<HTMLSelectElement> => (e) => {
    this.props.changeFormField('city', e.target.value)
  }

  createPhaseHandler = (): ChangeEventHandler<HTMLSelectElement> => (e) => {
    this.props.changeFormField('phase', e.target.value)
  }

  createDistrictHandler = (): ChangeEventHandler<HTMLSelectElement> => (e) => {
    const districtId = e.target.value
    this.props.changeFormField('schoolDistrict', districtId)
    if (districtId) {
      const district = _.find(this.props.districts, ['id', districtId])
      if (district?.country_code) {
        this.props.changeFormField('country', district.country_code)
      }
    }
  }

  createSchoolGradesHandler = (): ChangeEventHandler<HTMLInputElement> => (e) => {
    const schoolGrades = [...this.props.schoolGrades]
    const gradeIndex = schoolGrades.indexOf(e.target.value)
    if (gradeIndex >= 0) {
      schoolGrades.splice(gradeIndex, 1)
    } else {
      schoolGrades.push(e.target.value)
    }
    this.props.changeFormField('schoolGrades', schoolGrades)
    this.props.validateField('schoolGrades', schoolGrades, validations.schoolGrades)
  }

  createStartedRulerAtHandler = (): ChangeEventHandler<HTMLSelectElement> => (e) => {
    this.props.changeFormField('started_ruler_at', e.target.value)
  }

  createTotalStaffHandler = (): ChangeEventHandler<HTMLSelectElement> => (e) => {
    this.props.changeFormField('total_staff', parseInt(e.target.value, 10))
  }

  createIsRulerSchoolHandler = (): ChangeEventHandler<HTMLInputElement> => (e) => {
    this.props.changeFormField('started_ruler', e.target.value === 'Yes')
  }

  getCitiesByState = (stateId: string): void => {
    this.props.getCitiesByStateIdRequest({ urlParams: { stateId } })
  }

  getStatesByCountry = (countryId: string): void => {
    this.props.getStatesByCountryRequest({ urlParams: { countryId } })
  }

  createFieldHandler =
    (fieldName: string): ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      this.props.changeFormField(fieldName, e.target.value)
    }

  createFieldValidationHandler = (field: string, validationsKey: string) => (): void => {
    const value = (this.props as Record<string, unknown>)[field]
    this.props.validateField(field, value, validationsKey as keyof Validations)
  }

  renderDistrictSchoolInput(): React.ReactNode {
    return (
      <>
        <label htmlFor="school-district">{lang.dashboard.manageSchoolForm.schoolDistrict}</label>
        <select
          name=""
          id="school-district"
          value={this.props.schoolDistrict}
          onChange={this.createDistrictHandler()}
          onBlur={this.createFieldValidationHandler('schoolDistrict', validations.schoolDistrict)}>
          <option value={defaultFormData().schoolDistrict} />
          {!_.isEmpty(this.props.districts) ? (
            _.map(this.props.districts, (district) => (
              <option value={district.id} key={district.id}>
                {district.name}
              </option>
            ))
          ) : (
            <option value={this.props.schoolDistrict} key={this.props.schoolDistrict}>
              {this.props.schoolDistrictName}
            </option>
          )}
        </select>
        <span className={cn('form-error', { 'is-visible': this.props.errors.schoolDistrict })}>
          {this.props.errors.schoolDistrict}
        </span>
      </>
    )
  }

  renderGrades(): React.ReactNode {
    return (
      <fieldset className="inline-controls">
        <legend>{lang.dashboard.manageSchoolForm.gradesLabel}</legend>
        <div className="grades-list grid-x grid-margin-x small-up-4 medium-up-6 large-up-7">
          {_.map(this.props.grades, (grade: { id: string; name: string }) => (
            <label key={grade.id} className="cell" htmlFor={grade.id}>
              <input
                id={grade.id}
                type="checkbox"
                value={grade.id}
                checked={this.props.schoolGrades.indexOf(grade.id) >= 0}
                onChange={this.createSchoolGradesHandler()}
                onBlur={this.createFieldValidationHandler('schoolGrades', validations.schoolGrades)}
              />
              {grade.name}
            </label>
          ))}
        </div>
        <span className={cn('form-error', { 'is-visible': this.props.errors.schoolGrades })}>
          {this.props.errors.schoolGrades}
        </span>
      </fieldset>
    )
  }

  renderGeneralInfo(): React.ReactNode {
    return (
      <>
        <label htmlFor="school-name">{lang.dashboard.manageSchoolForm.schoolNameLabel}</label>
        <input
          type="text"
          id="school-name"
          value={this.props.schoolName}
          onChange={this.createFieldHandler('schoolName')}
          onBlur={this.createFieldValidationHandler('schoolName', validations.schoolName)}
        />
        <span className={cn('form-error', { 'is-visible': this.props.errors.schoolName })}>
          {this.props.errors.schoolName}
        </span>
        {this.renderDistrictSchoolInput()}
        {this.renderGrades()}
      </>
    )
  }

  renderSchoolProfile(): React.ReactNode {
    return (
      <>
        <fieldset>
          <legend>{lang.dashboard.manageSchoolForm.isRulerSchool}</legend>
          <input
            type="radio"
            name="ruler-school"
            value="Yes"
            id="ruler-school-yes"
            onChange={this.createIsRulerSchoolHandler()}
            checked={this.props.started_ruler}
          />
          <label className="text-capitalize" htmlFor="ruler-school-yes">
            {lang.yes}
          </label>
          <input
            type="radio"
            name="ruler-school"
            value="No"
            id="ruler-school-no"
            onChange={this.createIsRulerSchoolHandler()}
            checked={!this.props.started_ruler}
          />
          <label className="text-capitalize" htmlFor="ruler-school-no">
            {lang.no}
          </label>
        </fieldset>
        {this.props.started_ruler ? (
          <fieldset className="inline-controls">
            <label htmlFor="started_ruler_at">
              {lang.dashboard.manageSchoolForm.schoolRulerStart}
            </label>
            <select
              name="started_ruler_at"
              id="started_ruler_at"
              value={this.props.started_ruler_at}
              aria-label="Year"
              onChange={this.createStartedRulerAtHandler()}>
              <option value={defaultFormData().started_ruler_at} />
              {_.range(getYear(new Date()), 2011, -1).map((year) => (
                <option value={year} key={year}>
                  {year}
                </option>
              ))}
            </select>
            <span
              className={cn('form-error', {
                'is-visible': this.props.errors.started_ruler_at,
              })}>
              {this.props.errors.started_ruler_at}
            </span>
          </fieldset>
        ) : null}
        <fieldset className="inline-controls">
          <label htmlFor="total_staff">{lang.dashboard.manageSchoolForm.totalStaff}</label>
          <select
            name="total_staff"
            id="total_staff"
            value={this.props.total_staff}
            disabled={this.props.user != null && isUser.schoolImplementationTeam(this.props.user)}
            onChange={this.createTotalStaffHandler()}>
            <option value={defaultFormData().total_staff} />
            {_.map(
              [
                { value: 10, text: '10 - 50' },
                { value: 50, text: '51 - 100' },
                { value: 100, text: '101 - 250' },
                { value: 250, text: '251 - 500' },
                { value: 500, text: '> 500' },
              ],
              (item) => (
                <option value={item.value} key={item.value}>
                  {item.text}
                </option>
              ),
            )}
          </select>
          <span
            className={cn('form-error', {
              'is-visible': this.props.errors.total_staff,
            })}>
            {this.props.errors.total_staff}
          </span>
        </fieldset>
        <TrainingPicker
          changeFormField={this.props.changeFormField}
          validateField={(props: {
            field: string
            value: unknown
            validations: OfflineTrainingsValidations
          }) =>
            this.props.validateField(
              props as { field: string; value: unknown; validations: keyof Validations },
            )
          }
          trainings={this.props.offlineTrainings}
          validations={{ offlineTrainings: validations.offlineTrainings }}
          errors={this.props.errors}
        />
        <fieldset className="inline-controls">
          <label htmlFor="school-phase">
            {lang.dashboard.manageSchoolForm.implementationPhase}
          </label>
          <select
            name=""
            id="school-phase"
            value={this.props.phase}
            onChange={this.createPhaseHandler()}
            onBlur={this.createFieldValidationHandler('phase', validations.phase)}>
            <option value={defaultFormData().phase} />
            {_.map(this.props.phases, (phase: { id: string; name: string }) => (
              <option value={phase.id} key={phase.id}>
                {lang.dashboard.manageSchoolForm.phase} {phase.name}
              </option>
            ))}
          </select>
          <span className={cn('form-error', { 'is-visible': this.props.errors.phase })}>
            {this.props.errors.phase}
          </span>
        </fieldset>
        {this.props.user != null && isUser.rulerAnyAdmin(this.props.user) && (
          <div className="grid-x grid-margin-x">
            <div className="medium-12 cell">
              <label htmlFor="salesforce-id">
                {lang.dashboard.manageSchoolForm.salesforceId}
                <span className="option">{lang.optional}</span>
              </label>
              <input
                type="text"
                id="salesforce-id"
                value={this.props.salesforceId || ''}
                onChange={this.createFieldHandler('salesforceId')}
                onBlur={this.createFieldValidationHandler('salesforceId', validations.salesforceId)}
              />
              <span className={cn('form-error', { 'is-visible': this.props.errors.salesforceId })}>
                {this.props.errors.salesforceId}
              </span>
            </div>
          </div>
        )}
      </>
    )
  }

  renderSchoolAddress(): React.ReactNode {
    return (
      <fieldset>
        <legend>{lang.dashboard.manageSchoolForm.addressTitle}</legend>
        <div className="grid-x grid-margin-x">
          <div className="medium-6 cell">
            <label htmlFor="website-url">
              {lang.dashboard.locationForm.websiteLabel}
              <span className="option">{lang.optional}</span>
            </label>
            <input
              type="text"
              id="website-url"
              value={this.props.websiteUrl}
              onChange={this.createFieldHandler('websiteUrl')}
              onBlur={this.createFieldValidationHandler('websiteUrl', validations.websiteUrl)}
            />
            <span className={cn('form-error', { 'is-visible': this.props.errors.websiteUrl })}>
              {this.props.errors.websiteUrl}
            </span>
          </div>
          <div className="medium-6 cell">
            <label htmlFor="phone-number">
              {lang.dashboard.locationForm.phoneNumberLabel}
              <span className="option">{lang.optional}</span>
            </label>
            <input
              type="text"
              id="phone-number"
              value={this.props.phoneNumber}
              onChange={this.createFieldHandler('phoneNumber')}
              onBlur={this.createFieldValidationHandler('phoneNumber', validations.phoneNumber)}
            />
            <span className={cn('form-error', { 'is-visible': this.props.errors.phoneNumber })}>
              {this.props.errors.phoneNumber}
            </span>
          </div>
          <div className="medium-12 cell">
            <label htmlFor="country">{lang.dashboard.locationForm.countryLabel}</label>
            <select
              id="country"
              value={this.props.country}
              onChange={this.createCountryHandler()}
              onBlur={this.createFieldValidationHandler('country', validations.country)}>
              <option value={defaultFormData().country} />
              {_.map(
                sortCountriesWithUSFirst(this.props.availableCountries ?? []),
                (country: { code: string; name: string }) => (
                  <option value={country.code} key={country.code}>
                    {country.name}
                  </option>
                ),
              )}
            </select>
            <span className={cn('form-error', { 'is-visible': this.props.errors.country })}>
              {this.props.errors.country}
            </span>
          </div>
          <div className="medium-12 cell">
            <label htmlFor="street-address">
              {lang.dashboard.locationForm.addressLabel}
              <span className="option">{lang.optional}</span>
            </label>
            <input
              type="text"
              id="street-address"
              value={this.props.address}
              onChange={this.createFieldHandler('address')}
              onBlur={this.createFieldValidationHandler('address', validations.address)}
            />
            <span className={cn('form-error', { 'is-visible': this.props.errors.address })}>
              {this.props.errors.address}
            </span>
          </div>
          <div className="medium-12 cell">
            <label htmlFor="street-address-2">
              {lang.dashboard.locationForm.address2Label}
              <span className="option">{lang.optional}</span>
            </label>
            <input
              type="text"
              id="street-address-2"
              value={this.props.address2}
              onChange={this.createFieldHandler('address2')}
            />
          </div>
          {_.isEmpty(this.props.country) ? null : (
            <div className="medium-4 cell">
              <label htmlFor="state">{lang.dashboard.locationForm.stateLabel}</label>
              <select
                name=""
                id="state"
                value={this.props.state}
                onChange={this.createStateHandler()}
                onBlur={this.createFieldValidationHandler('state', validations.state)}>
                <option value={defaultFormData().state} />
                {_.map(this.props.availableStates, (state: { id: string; name: string }) => (
                  <option value={state.id} key={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
              <span className={cn('form-error', { 'is-visible': this.props.errors.state })}>
                {this.props.errors.state}
              </span>
            </div>
          )}
          {_.isEmpty(this.props.state) ? null : (
            <div className="medium-4 cell">
              <label htmlFor="city">{lang.dashboard.locationForm.cityLabel}</label>
              <select
                name=""
                id="city"
                value={this.props.city}
                onChange={this.createCityHandler()}
                onBlur={this.createFieldValidationHandler('city', validations.city)}>
                <option value={defaultFormData().city} />
                {_.map(
                  _.orderBy(this.props.availableCities, 'name'),
                  (city: { id: string; name: string; county?: string }) => (
                    <option value={city.id} key={city.id}>
                      {cityWithCounty(city.name, city.county)}
                    </option>
                  ),
                )}
              </select>
              <span className={cn('form-error', { 'is-visible': this.props.errors.city })}>
                {this.props.errors.city}
              </span>
            </div>
          )}
          <div className="medium-4 cell">
            <label htmlFor="zip-code">
              {lang.dashboard.locationForm.postalCodeLabel}
              <span className="option">{lang.optional}</span>
            </label>
            <input
              type="text"
              id="zip-code"
              value={this.props.postalCode}
              onChange={this.createFieldHandler('postalCode')}
              onBlur={this.createFieldValidationHandler('postalCode', validations.postalCode)}
            />
            <span className={cn('form-error', { 'is-visible': this.props.errors.postalCode })}>
              {this.props.errors.postalCode}
            </span>
          </div>
        </div>
      </fieldset>
    )
  }

  renderSchoolLogo(): React.ReactNode {
    if (!publicRuntimeConfig.FILESTACK_API) return null
    return (
      <ReactFilestack
        apikey={publicRuntimeConfig.FILESTACK_API}
        onSuccess={(response: { filesUploaded?: Array<{ url?: string }> }) => {
          const url = response.filesUploaded?.[0]?.url
          if (url) this.props.changeFormField('logoUrl', url)
        }}
        actionOptions={{
          transformations: {
            crop: {
              force: false,
            },
            circle: false,
          },
        }}
        customRender={({ onPick }: { onPick: () => void }) => (
          <button
            type="button"
            onClick={onPick}
            tabIndex={0}
            onKeyDown={onPick}
            className={cn('button hollow', { 'upload-image': this.props.logoUrl })}>
            {this.props.logoUrl ? (
              <img src={this.props.logoUrl} alt={this.props.schoolName || 'School'} />
            ) : null}
            {this.props.logoUrl
              ? lang.dashboard.manageSchoolForm.changeLogo
              : lang.dashboard.manageSchoolForm.uploadLogo}
          </button>
        )}
      />
    )
  }

  render(): React.ReactNode {
    return (
      <form action="">
        <span className={cn('form-error', { 'is-visible': this.props.errors.base })}>
          {this.props.errors.base}
        </span>
        {this.renderGeneralInfo()}
        {this.renderSchoolProfile()}
        {this.renderSchoolAddress()}
        {this.renderSchoolLogo()}
      </form>
    )
  }
}

export default connector(SchoolFormComponent)
